
function hashString(input: string): number {
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function seededRandom(seed: number): () => number {
  let state = seed
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 4294967296
  }
}

function timeBucket(minutes = 5): number {
  return Math.floor(Date.now() / (minutes * 60_000))
}

export function getLiveStatFluctuation(base: number, amplitude = 0.012): number {
  const bucket = timeBucket()
  const rand = seededRandom(bucket * 97 + base)
  const delta = (rand() - 0.5) * 2 * amplitude
  return Math.round(base * (1 + delta))
}

export function getLiveTickerValue(base: number, index: number): number {
  const bucket = timeBucket(1)
  const rand = seededRandom(bucket * 131 + index * 17 + base)
  const wave = Math.sin((bucket + index) / 3) * 0.008
  const jitter = (rand() - 0.5) * 0.004
  return Number((base * (1 + wave + jitter)).toFixed(2))
}

export type ScanStepId = "scanning" | "sanctions" | "analyzing" | "calculating"

export type ScanResult = {
  address: string
  riskScore: number
  riskLevel: "low" | "medium" | "high" | "critical"
  flags: string[]
  lastSeen: string
  volumeUsd: number
}

export function resolveScanResult(address: string): ScanResult {
  const normalized = address.trim().toLowerCase()
  const rand = seededRandom(hashString(normalized))
  const riskScore = Math.round(18 + rand() * 74)
  const riskLevel =
    riskScore >= 80
      ? "critical"
      : riskScore >= 60
        ? "high"
        : riskScore >= 35
          ? "medium"
          : "low"

  const flagPool = [
    "sanctions_proximity",
    "mixer_exposure",
    "high_velocity",
    "darknet_cluster",
    "jurisdiction_alert",
    "scam_pattern",
  ] as const

  const flagCount = 2 + Math.floor(rand() * 3)
  const flags: string[] = []
  for (let i = 0; i < flagCount; i += 1) {
    const flag = flagPool[Math.floor(rand() * flagPool.length)] ?? flagPool[0]
    if (!flags.includes(flag)) flags.push(flag)
  }

  const hoursAgo = Math.floor(rand() * 72)
  const lastSeen = new Date(Date.now() - hoursAgo * 3_600_000).toISOString()
  const volumeUsd = Number((rand() * 4_500_000 + 12_000).toFixed(0))

  return {
    address: normalized,
    riskScore,
    riskLevel,
    flags,
    lastSeen,
    volumeUsd,
  }
}

export const MAP_POINTS = [
  { name: "Singapore", coordinates: [103.8198, 1.3521] as [number, number] },
  { name: "Dubai", coordinates: [55.2708, 25.2048] as [number, number] },
  { name: "London", coordinates: [-0.1276, 51.5074] as [number, number] },
  { name: "New York", coordinates: [-74.006, 40.7128] as [number, number] },
  { name: "Hong Kong", coordinates: [114.1694, 22.3193] as [number, number] },
] as const

export const ENTERPRISE_LOGOS = [
  "Meridian Capital",
  "Atlas Compliance",
  "Northgate Treasury",
  "CipherBridge",
  "Helios Exchange",
  "Summit Custody",
  "Vertex Analytics",
  "Pacific Ledger",
] as const

export const RISK_CARD_KEYS = [
  "sanctions",
  "fraud",
  "mixer",
  "exchange",
  "whale",
  "reputation",
] as const

export type RiskCardKey = (typeof RISK_CARD_KEYS)[number]

export function getRiskCardScore(key: RiskCardKey): number {
  const bucket = timeBucket(2)
  const rand = seededRandom(hashString(key) + bucket)
  return Math.round(12 + rand() * 78)
}
